import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { courseService } from '../../api';
import toast from 'react-hot-toast';
import {
    Plus, Edit2, Trash2, BookOpen, Clock, User,
    Home, Copy, Layers, Layout, LayoutGrid, List,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListToolbar from '../../components/list/ListToolbar';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { ShortCardGrid, ListSection } from '../../components/list/ShortCard';
import { AdminCourseShortCard } from '../../components/list/ShortCardVariants';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function AdminCourses() {
    const [courses, setCourses] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [levelFilter, setLevelFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [viewMode, setViewMode] = useState('grid');
    const navigate = useNavigate();

    const fetchCourses = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (levelFilter !== 'all') params.level = levelFilter;
            if (searchTerm) params.search = searchTerm;

            const res = await courseService.getCourses(params);
            const { results, count } = parsePaginated(res);
            setCourses(results);
            setTotalCount(count);
        } catch (err) {
            console.error('Fetch error:', err);
            toast.error('Failed to load courses from server');
        } finally {
            setLoading(false);
        }
    }, [levelFilter, searchTerm, page, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [levelFilter, searchTerm]);

    useEffect(() => {
        fetchCourses();
    }, [levelFilter, fetchCourses]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCourses();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, fetchCourses]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this course? This will remove all associated subjects and lessons.')) return;

        try {
            await courseService.deleteCourse(id);
            toast.success('Course deleted successfully');
            fetchCourses();
        } catch (err) {
            toast.error('Failed to delete course');
        }
    };

    const levelSegment = (
        <div className="ux-segment">
            {['all', 'beginner', 'intermediate', 'advanced'].map((lvl) => (
                <button
                    key={lvl}
                    type="button"
                    className={levelFilter === lvl ? 'is-active' : ''}
                    onClick={() => setLevelFilter(lvl)}
                >
                    {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                </button>
            ))}
        </div>
    );

    const viewToggle = (
        <div className="ux-list-view-toggle">
            <button
                type="button"
                className={viewMode === 'list' ? 'is-active' : ''}
                onClick={() => setViewMode('list')}
                aria-label="List view"
            >
                <List size={18} />
            </button>
            <button
                type="button"
                className={viewMode === 'grid' ? 'is-active' : ''}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
            >
                <LayoutGrid size={18} />
            </button>
        </div>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="LMS Operations"
                    kickerIcon={Layout}
                    title="Available Courses"
                    description="Manage your academic catalog and track program availability across campuses."
                    actions={(
                        <Link to="/admin/courses/new" className="ux-btn-sm ux-btn-primary" style={{ padding: '0.65rem 1.25rem', fontSize: '0.875rem' }}>
                            <Plus size={18} />
                            Create New Course
                        </Link>
                    )}
                />

                <ListToolbar
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search curriculum…"
                    filters={levelSegment}
                    viewToggle={viewToggle}
                />

                {loading ? (
                    <ListLoadingState label="Loading courses…" />
                ) : courses.length === 0 ? (
                    <ListEmptyState
                        icon={BookOpen}
                        title={searchTerm || levelFilter !== 'all' ? 'No courses match your filters' : 'No courses yet'}
                        description={
                            searchTerm || levelFilter !== 'all'
                                ? 'Try adjusting search or level filters.'
                                : 'Create your first course to start building your catalog.'
                        }
                        action={
                            (searchTerm || levelFilter !== 'all') ? (
                                <button type="button" className="ux-btn-sm ux-btn-ghost" onClick={() => { setSearchTerm(''); setLevelFilter('all'); }}>
                                    Reset filters
                                </button>
                            ) : null
                        }
                    />
                ) : viewMode === 'list' ? (
                    <DataRowList>
                        {courses.map((course, index) => (
                            <DataRow
                                key={course.id}
                                index={index}
                                leading={(
                                    <div className="ux-row-thumb">
                                        {course.thumbnail ? (
                                            <img src={course.thumbnail} alt="" />
                                        ) : (
                                            <BookOpen size={22} />
                                        )}
                                    </div>
                                )}
                                title={course.title || 'Untitled Course'}
                                subtitle={course.description || 'No description'}
                                meta={(
                                    <>
                                        <span><Clock size={12} /> {course.duration || '—'} min</span>
                                        <span><User size={12} /> {course.instructor_name || 'N/A'}</span>
                                        <span><Home size={12} /> {course.campus_name || course.campus || 'Campus'}</span>
                                    </>
                                )}
                                badges={(
                                    <>
                                        <StatusPill status={course.level} />
                                        <StatusPill status={course.status} label={(course.status || 'draft').replace(/_/g, ' ')} />
                                    </>
                                )}
                                actions={(
                                    <>
                                        <button type="button" className="ux-btn-sm ux-btn-primary" onClick={() => navigate(`/admin/courses/${course.id}/content`)}>
                                            <Layers size={14} /> Manage
                                        </button>
                                        <button type="button" className="ux-icon-btn" onClick={() => navigate(`/admin/courses/${course.id}/edit`)} title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button type="button" className="ux-icon-btn" onClick={() => navigate('/admin/courses/new', { state: { duplicateFrom: course.id } })} title="Duplicate">
                                            <Copy size={16} />
                                        </button>
                                        <button type="button" className="ux-icon-btn danger" onClick={() => handleDelete(course.id)} title="Delete">
                                            <Trash2 size={16} />
                                        </button>
                                    </>
                                )}
                            />
                        ))}
                    </DataRowList>
                ) : (
                    <ListSection title="Course catalog" description="Browse and manage programs across your campuses.">
                        <ShortCardGrid>
                            {courses.map((course, index) => (
                                <AdminCourseShortCard
                                    key={course.id}
                                    course={course}
                                    index={index}
                                    onManage={() => navigate(`/admin/courses/${course.id}/content`)}
                                    onEdit={() => navigate(`/admin/courses/${course.id}/edit`)}
                                    onDelete={() => handleDelete(course.id)}
                                />
                            ))}
                        </ShortCardGrid>
                    </ListSection>
                )}

                {!loading && courses.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    />
                )}
            </main>
        </DashboardLayout>
    );
}

export default AdminCourses;
